import React, { useState, useContext } from "react";
import { Form, Button, Alert, Spinner, Card } from "react-bootstrap";
import NumberFormat from "react-number-format";
import {
  IconUserPlus,
  IconSearch,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import NotificationsContext from "../../../../contexts/NotificationsContext";
import {
  formatPhoneForAPI,
  formatPhoneNumber,
} from "../../../../helpers/Helpers";
import { api } from "../../../../api/Middleware";
import { createCustomer } from "../../../../screens/Customers/services/CustomerServices";
import { CUSTOMERS_URL } from "../../../../api/Urls";

const ClientSearch = ({ onClientSelected }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [searchPhone, setSearchPhone] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);

  // Estados para criação de cliente
  const [newClientName, setNewClientName] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newClientCPF, setNewClientCPF] = useState("");
  const [newClientBirthday, setNewClientBirthday] = useState("");

  // Buscar cliente por telefone
  const handleSearchClient = async () => {
    if (!searchPhone) {
      setNotify({
        title: "Atenção",
        message: "Digite um telefone para buscar",
        type: "warning",
        visible: true,
      });
      return;
    }

    setSearchLoading(true);
    setSearchAttempted(true);

    try {
      const phoneForAPI = formatPhoneForAPI(searchPhone);

      // Usar o endpoint de busca por telefone
      const response = await api.get(CUSTOMERS_URL + `search_by_phone/`, {
        params: { phone: phoneForAPI },
      });

      if (response.data && response.data.found && response.data.customer) {
        const client = response.data.customer;
        setSelectedClient(client);

        // Callback para o componente pai
        if (onClientSelected) {
          onClientSelected(client);
        }

        // Mostrar em qual campo foi encontrado
        const foundInField =
          response.data.customer.found_in_field === "phone_primary"
            ? "telefone principal"
            : "telefone secundário";

        setNotify({
          title: "Cliente encontrado!",
          message: `${client.full_name} foi encontrado pelo ${foundInField}`,
          type: "success",
          visible: true,
        });
      } else {
        setSelectedClient(null);
        setNotify({
          title: "Atenção",
          message: "Nenhum cliente encontrado com este telefone.",
          type: "warning",
          visible: true,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      setSelectedClient(null);

      // Se erro 400, mostrar mensagem específica
      if (error.response?.status === 400) {
        setNotify({
          title: "Formato inválido",
          message: error.response.data.error || "Formato de telefone inválido",
          type: "warning",
          visible: true,
        });
      } else {
        setNotify({
          title: "Erro na busca",
          message: "Erro ao buscar cliente. Tente novamente.",
          type: "error",
          visible: true,
        });
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Criar novo cliente
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      setNotify({
        title: "Atenção",
        message: "Nome do cliente é obrigatório para automações",
        type: "warning",
        visible: true,
      });
      return;
    }

    if (!newClientPhone.trim()) {
      setNotify({
        title: "Atenção",
        message: "Telefone do cliente é obrigatório para automações",
        type: "warning",
        visible: true,
      });
      return;
    }

    setCreateLoading(true);

    try {
      let clientData = {
        full_name: newClientName,
        phone_primary: formatPhoneForAPI(newClientPhone),
        person_type: "PF",
        status: "active",
        customer_type: "new",
        is_whatsapp_active: true,
        accepts_marketing: true,
      };

      if (newClientCPF) clientData["cpf"] = newClientCPF;
      if (newClientBirthday) {
        clientData["birth_date"] = newClientBirthday
          .split("-")
          .reverse()
          .join("-");
      }

      const response = await createCustomer(clientData);
      const newClient = response.data;

      setSelectedClient(newClient);

      // Callback para o componente pai
      if (onClientSelected) {
        onClientSelected(newClient);
      }

      setShowCreateForm(false);
      setNewClientName("");
      setNewClientPhone("");
      setNewClientCPF("");
      setNewClientBirthday("");

      setNotify({
        title: "Cliente criado!",
        message: `${newClient.full_name} foi criado e selecionado`,
        type: "success",
        visible: true,
      });
    } catch (error) {
      console.error("Erro ao criar cliente:", error);

      let errorMessage = "Erro ao criar cliente";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "object") {
          errorMessage = Object.values(errorData).flat().join(", ");
        } else {
          errorMessage = errorData.detail || errorData.message || errorMessage;
        }
      }

      setNotify({
        title: "Erro ao criar cliente",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // Limpar seleção
  const handleClearSelection = () => {
    setSelectedClient(null);
    setSearchPhone("");
    setSearchAttempted(false);
    setShowCreateForm(false);

    // Callback para limpar no componente pai
    if (onClientSelected) {
      onClientSelected(null);
    }
  };

  // Mostrar formulário de criação
  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    if (searchPhone) {
      setNewClientPhone(searchPhone);
    }
  };

  return (
    <div className="client-search-container mb-3">
       
          {!selectedClient ? (
            <>
              {/* Busca de Cliente */}
              <div className="mb-3">
                <Form.Label className="small fw-bold text-primary">
                  Buscar Cliente por Telefone
                </Form.Label>
                <div className="input-group">
                  <NumberFormat
                    format="(##) #####-####"
                    mask="_"
                    className="form-control"
                    placeholder="(00) 00000-0000"
                    value={searchPhone}
                    onValueChange={(values) => setSearchPhone(values.value)}
                    style={{ border: "1px solid var(--primary)" }}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={handleSearchClient}
                    disabled={searchLoading}
                    className="d-flex align-items-center"
                  >
                    {searchLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <IconSearch size={16} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Opções quando não encontrou cliente */}
              {searchAttempted && !selectedClient && (
                <Alert variant="info" className="py-2 mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <small>Cliente não encontrado. Deseja criar um novo?</small>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleShowCreateForm}
                      className="d-flex align-items-center"
                    >
                      <IconUserPlus size={14} className="me-1" />
                      Criar Cliente
                    </Button>
                  </div>
                </Alert>
              )}

              {/* Formulário de criação de cliente */}
              {showCreateForm && (
                <div className="border rounded p-3 bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 text-success">
                      <IconUserPlus size={18} className="me-2" />
                      Novo Cliente
                    </h6>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowCreateForm(false)}
                      className="p-0 text-muted"
                    >
                      <IconX size={16} />
                    </Button>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="small">
                          Nome Completo *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={newClientName}
                          onChange={(e) => setNewClientName(e.target.value)}
                          placeholder="Digite o nome completo"
                          size="sm"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="small">Telefone *</Form.Label>
                        <NumberFormat
                          format="(##) #####-####"
                          mask="_"
                          className="form-control form-control-sm"
                          placeholder="(00) 00000-0000"
                          value={newClientPhone}
                          onValueChange={(values) =>
                            setNewClientPhone(values.value)
                          }
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="small">CPF</Form.Label>
                        <NumberFormat
                          format="###.###.###-##"
                          mask="_"
                          className="form-control form-control-sm"
                          placeholder="opcional..."
                          value={newClientCPF}
                          onValueChange={(values) =>
                            setNewClientCPF(values.value)
                          }
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="small">
                          Data de nascimento
                        </Form.Label>
                        <NumberFormat
                          format="##-##-####"
                          mask="_"
                          className="form-control form-control-sm"
                          placeholder="opcional..."
                          value={newClientBirthday}
                          onValueChange={(values) => {
                            setNewClientBirthday(values.formattedValue);
                          }}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleCreateClient}
                      disabled={createLoading}
                      className="d-flex align-items-center"
                    >
                      {createLoading ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                      ) : (
                        <IconCheck size={14} className="me-1" />
                      )}
                      {createLoading ? "Criando..." : "Criar Cliente"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Cliente Selecionado */
            <Alert variant="success" className="mb-0">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-bold">{selectedClient.full_name}</div>
                  <small className="text-muted">
                    {formatPhoneNumber(selectedClient.phone_primary)}
                  </small>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleClearSelection}
                  className="d-flex align-items-center"
                >
                  <IconX size={14} className="me-1" />
                  Remover
                </Button>
              </div>
            </Alert>
          )}
    </div>
  );
};

export default ClientSearch;